import React, { useState } from 'react';

import usePageTitle from '../../../utils/hooks/use_page_title';

import ActivityLog from '../../ActivityLog/ActivityLog';
import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';
import PageLayout from '../../pages/PageLayout';
import PipelineCreationFlow from '../PipelineCreationFlow/PipelineCreationFlow';
import PipelineList from './PipelineList';
import PrimaryContent from '../../pages/layouts/PrimaryContent';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';
import SecondaryContent from '../../pages/layouts/SecondaryContent';

import { getCommentsWithPipelines } from '../../../utils/product_utils';
import { getPipelineActivityData } from '../../ActivityLog/activity-utils';

import { PipelineSearchProvider, usePipelineSearch } from '../PipelineSearchContext';
import { ReadOnlyPipelineCommentsProvider, useCommentContext } from '../../contexts/CommentData/CommentData';
import { useClientContext } from '../../contexts/ClientData/ClientData';
import { useUserContext } from '../../contexts/UserData/UserData';

import { ModelSearchProvider } from '../../models/ModelSearchContext';
import { PipelineCreationProvider } from '../PipelineCreationContext';
import WithTooltip from '../../common/TooltipWrapper/TooltipWrapper';
import useWorkspacePermissions from '../../../utils/hooks/use_workspace_permissions';

const statusItems = [
  {
    id: 'all',
    label: 'All Pipelines'
  },
  {
    id: 'Active',
    label: 'Only Active',
  },
  {
    id: 'Inactive',
    label: 'Only Inactive',
  }
]

const StatusFilterDropdown = ({
  filteredStatus, onFilterChange
}) => (
  <BasicDropdown
    dropdownLabel=""
    values={statusItems}
    selectedValue={filteredStatus}
    onValueSelected={onFilterChange}
  />
);

const filterPipelinesByStatus = (pipelines, filteredStatus) => {
  if (!pipelines || !pipelines?.length || !filteredStatus || filteredStatus === 'all') {
    return pipelines;
  }
  return pipelines.filter(({ status }) => status === filteredStatus)
}

export const PipelineListView = ({
  pipelines,
  isLoading,
  hasSearchQueryError,
}) => (
  <div className="mt-2 bg-white border border-border-dark rounded relative">
    {
      isLoading ? (
        <LoadingSpinner animationKey="pipeline-list-spinner" size="medium" useOverlay />
      ) : null
    }
    {
      hasSearchQueryError ? (
        <ErrorSlate
          errorText="Something went wrong loading your pipelines"
        />
      ) : (
        <PipelineList
          pipelines={pipelines}
        />
      )
    }
  </div>
)

export const PipelineSearchPanel = () => {
  const {
    pipelines,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  } = usePipelineSearch();

  const [filteredStatus, setFilteredStatus] = useState('all');
  const filteredPipelines = filterPipelinesByStatus(pipelines, filteredStatus);

  return (
    <div>
      <div className="w-full flex flex-grow-2 justify-start items-center h-full">
        <SearchInput
          className="relative flex-grow border border-border-dark rounded py-2 pl-10 mr-2 bg-white"
          ariaLabel="Search by pipeline name or tag"
          placeholder="Search by pipeline name or tag"
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
        />
        <StatusFilterDropdown filteredStatus={filteredStatus} onFilterChange={setFilteredStatus} />
      </div>
      <div className="mt-2">
        <PipelineListView
          pipelines={filteredPipelines}
          isLoading={loading}
          hasSearchQueryError={error}
        />
      </div>
    </div>
  )
}

const ActivityLogWrapper = () => {
  const { isAuthEnabled } = useClientContext();
  const { userData } = useUserContext();
  const { comments: commentsWithUsers, error, loading } = useCommentContext();
  const { pipelines = [] } = usePipelineSearch();

  if (!isAuthEnabled) {
    return null;
  }

  const activityData = getPipelineActivityData({
    comments: getCommentsWithPipelines(pipelines, commentsWithUsers),
    currentUser: userData,
  })
  return (
    <ActivityLog
      id="pipeline-search-activity-log"
      height={480}
      activityData={activityData}
      commentError={error}
      areCommentsLoading={loading}
      shouldShowPipelineName
      disableScrollFading
    />
  )
}

const PipelineCreationPanel = () => {
  const [isCreateMenuOpen, setCreateMenuOpen] = useState(false);
  const { canModify } = useWorkspacePermissions();

  return (
    <>
      <CardView className="mb-2">
        <WithTooltip label="Insufficient workspace permissions" showTooltip={!canModify}>
          <Button
            variant="primary"
            size="large"
            onClick={() => setCreateMenuOpen(true)}
            disabled={!canModify}
          >
            Create Pipeline
          </Button>
        </WithTooltip>
      </CardView>
      {
        isCreateMenuOpen ? (
          <ModalOverlay
            animationKey="create-pipeline-overlay"
            active={isCreateMenuOpen}
            onModalToggle={setCreateMenuOpen}
          >
            <div className="w-1/2 h-1/2">
              <PipelineCreationProvider>
                <ModelSearchProvider>
                  <PipelineCreationFlow onClose={() => setCreateMenuOpen(false)} />
                </ModelSearchProvider>
              </PipelineCreationProvider>
            </div>
          </ModalOverlay>
        ) : null
      }
    </>
  )
}

export const PipelinesIndex = () => {
  usePageTitle('Pipelines | Wallaroo');

  return (
    <PageLayout>
      <PipelineSearchProvider>
        <PrimaryContent>
          <PipelineSearchPanel />
        </PrimaryContent>
        <SecondaryContent>
          <PipelineCreationPanel />
          <ReadOnlyPipelineCommentsProvider>
            <ActivityLogWrapper />
          </ReadOnlyPipelineCommentsProvider>
        </SecondaryContent>
      </PipelineSearchProvider>
    </PageLayout>
  );
};

export default PipelinesIndex;
